/* eslint-disable complexity */
import {
  PredictiveAirports,
  PredictiveSearchList,
  PredictiveSearchResponse
} from '../../models/core-apis/predictive-search.interfaces';

const levenshteinDistance = (a: string, b: string): number => {
  const matrix = Array(b.length + 1)
    .fill(null)
    .map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,
        matrix[j - 1][i] + 1,
        matrix[j - 1][i - 1] + cost
      );
    }
  }

  return matrix[b.length][a.length];
};

const isFuzzyMatch = (text: string, search: string, threshold = 1): boolean => {
  if (text.includes(search)) return true;
  if (Math.abs(text.length - search.length) > threshold) return false;
  return levenshteinDistance(text, search) <= threshold;
};

export const getFilteredPredictiveSearchList = (
  predictiveSearchResponse: PredictiveSearchResponse,
  predictiveSearchString: string
): PredictiveSearchResponse => {
  if (!predictiveSearchResponse) {
    return [];
  }
  const searchString = predictiveSearchString.toLowerCase().trim();
  const searchWords = searchString.split(/\s+/);

  return predictiveSearchResponse.filter((airport: PredictiveAirports) => {
    const airportCode = airport.AIRPORT_CODE_LOWERCASE.toLowerCase();
    const cityName = airport.CITY_NAME.toLowerCase();
    const cityServe = airport.CITY_SERVE.toLowerCase();

    // Check for exact string match first
    if (
      airportCode.includes(searchString) ||
      cityName.includes(searchString) ||
      cityServe.includes(searchString)
    ) {
      return true;
    }

    // Check fuzzy match for complete string
    if (
      isFuzzyMatch(airportCode, searchString) ||
      isFuzzyMatch(cityName, searchString) ||
      isFuzzyMatch(cityServe, searchString)
    ) {
      return true;
    }

    // Fall back to individual word matching with fuzzy search
    const fields = [airportCode, cityName, cityServe];
    return searchWords.every((word) =>
      fields.some((field) => isFuzzyMatch(field, word))
    );
  });
};

export const getPredictiveSearchList = (
  predictiveSearchResponse: PredictiveSearchResponse
): PredictiveSearchList => {
  return {
    airportList: predictiveSearchResponse.map(
      (airport: PredictiveAirports) => ({
        id: airport.AIRPORT_CODE,
        value: airport.COUNTRY
          ? `${airport.CITY_NAME}, ${airport.COUNTRY}`
          : `${airport.CITY_NAME}`,
        label: airport.AIRPORT_CODE
      })
    )
  };
};
